import {
  AlgoState,
  AlgoStatus,
  AlgoType,
  OptionsMmLifecycle,
  OptionsMmMode,
} from '@/types/generated/algo'

const RUNNING = new Set([
  AlgoStatus.ALGO_STATUS_PENDING,
  AlgoStatus.ALGO_STATUS_IN_PROGRESS,
  AlgoStatus.ALGO_STATUS_INTERRUPTED,
])

export function isLive(s: AlgoState): boolean {
  return (
    RUNNING.has(s.status) &&
    s.algoData?.optionsMm?.lifecycle !== OptionsMmLifecycle.OPTIONS_MM_LIFECYCLE_HALTED
  )
}

/**
 * The algo service holds a task for the instance, so an update reaches its
 * engine. An interrupted one is waiting to resume on another host and a halted
 * one has stopped quoting; the service drops an update to either without a
 * reply, so the dialog would only time out.
 */
export function canUpdate(s: AlgoState): boolean {
  return (
    (s.status === AlgoStatus.ALGO_STATUS_PENDING ||
      s.status === AlgoStatus.ALGO_STATUS_IN_PROGRESS) &&
    s.algoData?.optionsMm?.lifecycle !== OptionsMmLifecycle.OPTIONS_MM_LIFECYCLE_HALTED
  )
}

export interface InstanceGroup {
  accountId: string
  instances: AlgoState[]
  /** Open orders reserved by the group's live instances. */
  reservedTotal: number
  /** The venue's open-order cap for the account; `null` until an instance reports capabilities. */
  openOrderCap: number | null
  /**
   * Matching requests/s the group's live instances may spend. Shadow instances
   * never send, and their share is priced as if they joined, so they are left out.
   */
  budgetShareTotal: number
}

function totals(instances: AlgoState[]) {
  let reservedTotal = 0
  let budgetShareTotal = 0
  let openOrderCap: number | null = null
  for (const s of instances) {
    const mm = s.algoData?.optionsMm
    if (!mm || !isLive(s)) continue
    reservedTotal += mm.openOrdersReserved
    if (mm.mode !== OptionsMmMode.OPTIONS_MM_MODE_SHADOW) {
      budgetShareTotal += Number(mm.budgetShare) || 0
    }
    // Every instance on one account mirrors the same venue cap.
    if (mm.capabilities) openOrderCap = Math.max(openOrderCap ?? 0, mm.capabilities.openOrderCap)
  }
  return { reservedTotal, budgetShareTotal, openOrderCap }
}

/** Options-MM instances grouped by account (spec: the instances list). */
export function groupInstances(
  states: AlgoState[],
  opts: { includeEnded: boolean }
): InstanceGroup[] {
  const by = new Map<string, AlgoState[]>()
  for (const s of states) {
    if (s.algoType !== AlgoType.ALGO_TYPE_OPTIONS_MM) continue
    if (!opts.includeEnded && !isLive(s)) continue
    const account = s.accountIds[0] ?? ''
    by.set(account, [...(by.get(account) ?? []), s])
  }
  return [...by.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([accountId, instances]) => ({
      accountId,
      // createdAt is epoch ms as an int64 string.
      instances: instances.sort(
        (x, y) => Number(isLive(y)) - Number(isLive(x)) || Number(y.createdAt) - Number(x.createdAt)
      ),
      ...totals(instances),
    }))
}
